import type { ReactNode } from 'react';
import { LogOut, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { logout } from './api';

/**
 * Shared shell for every authenticated admin page: thin top bar with the
 * brand mark, a "Logout" button, and a content slot. Future admin pages
 * (push composer, feature flags, …) drop in as additional routes that
 * render inside this layout.
 */
export default function AdminLayout({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  const navigate = useNavigate();

  async function handleLogout() {
    try {
      await logout();
    } finally {
      navigate('/admin/login', { replace: true });
    }
  }

  return (
    <div className="min-h-screen bg-brand-ink text-slate-100 font-sans">
      <header className="border-b border-white/5 bg-brand-panel/60 backdrop-blur">
        <div className="container-page flex items-center justify-between py-4">
          <div className="flex items-center gap-2.5">
            <img src="/favicon.png" alt="" className="h-7 w-7" />
            <div>
              <p className="font-display text-base font-bold leading-tight">
                Dinar Pulse Admin
              </p>
              <p className="text-xs text-slate-500">{title}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden items-center gap-1.5 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-2.5 py-1 text-xs font-medium text-emerald-300 sm:inline-flex">
              <ShieldCheck className="h-3.5 w-3.5" />
              Signed in
            </span>
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-slate-200 transition-colors hover:bg-white/10"
            >
              <LogOut className="h-3.5 w-3.5" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="container-page py-10">{children}</main>
    </div>
  );
}
